import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useCmNewsApi from "hooks/api/private/useCmNewsApi";
import useSnackbar from "hooks/useSnackbar";

import { CmNewsListItem } from "types/api/cmNews";

export type CmNewsListHook = {
    navigateToNewNews: () => void;
    onSearchFilterChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    dataTableHook: DataTableHook<CmNewsListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useCmNewsList(): CmNewsListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "cm-news-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);
                newForm.search.title = "" + event.target.value;
                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            language: {
                key: "language",
                operation: FilterOperation.Eq,
                defaultValue: "it",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "cm-news-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllCmNews, deleteCmNews } = useCmNewsApi();

    const navigate = useNavigate();

    const navigateToEditNews = React.useCallback(
        (row: CmNewsListItem) => {
            navigate(makeUrl(`${row.id}/modifica`));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina notizia",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteNewsButton = React.useCallback(
        (row: CmNewsListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare la notizia?", () => {
                const [apiCall] = deleteCmNews({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Notizia eliminata con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione della notizia"));
            });
        },
        [enqueueSuccessSnackbar, setTriggerReloadTable, enqueueErrorSnackbar, openConfirmAlert, deleteCmNews],
    );

    const columns: Array<Column<CmNewsListItem>> = React.useMemo(
        () => [
            {
                id: "title",
                header: "Titolo",
                render: (row: CmNewsListItem) => row.title,
            },
            {
                id: "date_from",
                header: "Data da",
                width: "130px",
                render: (row: CmNewsListItem) => row.date_from,
            },
            {
                id: "date_to",
                header: "Data a",
                width: "130px",
                render: (row: CmNewsListItem) => row.date_to,
            },
            {
                id: "enabled",
                header: "Visibile",
                width: "130px",
                render: (row: CmNewsListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: CmNewsListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditNews}
                        onDelete={deleteNewsButton}
                    />
                ),
            },
        ],
        [navigateToEditNews, deleteNewsButton],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const dataTableHook = useDataTableWithMemory<CmNewsListItem>({
        apiCall: getAllCmNews,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "cm-news-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();
            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewNews = React.useCallback(() => {
        navigate("/cm-notizie/nuova");
    }, [navigate]);

    return React.useMemo(
        (): CmNewsListHook => ({
            onSearchFilterChange,
            navigateToNewNews,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [onSearchFilterChange, navigateToNewNews, filterSearchHook, alertConfirmHook, staticFilterHook, dataTableHook],
    );
}

export default useCmNewsList;
